"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { useMutation } from "@apollo/client/react";
import Image from "next/image";

// MUI
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

// GraphQL
import { gql } from "@apollo/client";

// Components
import CloudinaryUploadWidget from "@/components/auth/CloudinaryUploadWidget";

// ── env ──────────────────────────────────────────────────────
const API_BASE = (
  process.env.NEXT_PUBLIC_API_ORIGIN || "http://localhost:5000"
).replace(/\/$/, "");

const APP_ORIGIN = (
  process.env.NEXT_PUBLIC_APP_ORIGIN || "http://localhost:3000"
).replace(/\/$/, "");

const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "";

// ── GraphQL mutations ─────────────────────────────────────────
const LOGIN_MUTATION = gql`
  mutation Login(
    $email: String!
    $password: String!
    $rememberMe: Boolean
    $captchaToken: String
  ) {
    login(
      email: $email
      password: $password
      rememberMe: $rememberMe
      captchaToken: $captchaToken
    ) {
      id
      email
      firstname
      lastname
      thumbnail
      defaultBrandId
      brands {
        id
        name
        slug
      }
    }
  }
`;

const REGISTER_MUTATION = gql`
  mutation Register(
    $email: String!
    $password: String!
    $firstname: String!
    $lastname: String!
    $mobile: String!
    $thumbnail: String!
    $brandName: String
    $captchaToken: String
  ) {
    register(
      email: $email
      password: $password
      firstname: $firstname
      lastname: $lastname
      mobile: $mobile
      thumbnail: $thumbnail
      brandName: $brandName
      captchaToken: $captchaToken
    ) {
      id
      email
      firstname
      lastname
      thumbnail
      defaultBrandId
      brands {
        id
        name
        slug
      }
    }
  }
`;

const FORGOT_PASSWORD_MUTATION = gql`
  mutation ($email: String!) {
    forgotpassword(email: $email)
  }
`;

const RESET_PASSWORD_MUTATION = gql`
  mutation ($userId: String!, $jwt: String!) {
    resetpassword(userId: $userId, jwt: $jwt)
  }
`;

const RESET_PASSWORD_UPDATE_MUTATION = gql`
  mutation ($userId: String!, $jwt: String!, $password: String!) {
    resetpasswordupdate(userId: $userId, jwt: $jwt, password: $password)
  }
`;

const VERIFY_EMAIL_MUTATION = gql`
  mutation ($userId: String!, $token: String!) {
    verifyEmail(userId: $userId, token: $token)
  }
`;

// ── helpers ───────────────────────────────────────────────────
const emailRe = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const mobileRe = /^\d{10}$/;
const passwordRe = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?!.*\s).{8,}$/;

const redirectToFeed = () => {
  window.location.href = `${APP_ORIGIN}/feed`;
};

// ── reCAPTCHA helper ──────────────────────────────────────────
const loadRecaptcha = (): Promise<void> => {
  return new Promise((resolve) => {
    if (!RECAPTCHA_SITE_KEY) return resolve();
    if ((window as any).grecaptcha) return resolve();
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.onload = () => resolve();
    document.head.appendChild(script);
  });
};

const executeRecaptcha = async (action: string): Promise<string> => {
  if (!RECAPTCHA_SITE_KEY) return "";
  await loadRecaptcha();
  return new Promise((resolve) => {
    (window as any).grecaptcha.ready(async () => {
      const token = await (window as any).grecaptcha.execute(
        RECAPTCHA_SITE_KEY,
        { action },
      );
      resolve(token);
    });
  });
};

// ── Password strength ─────────────────────────────────────────
type StrengthLevel = 0 | 1 | 2 | 3 | 4;

interface StrengthResult {
  level: StrengthLevel;
  label: string;
  color: string;
}

function getPasswordStrength(password: string): StrengthResult {
  if (!password || password.length < 8) {
    return { level: 0, label: "Too short", color: "#e53935" };
  }
  let score = 0;
  if (/[a-z]/.test(password)) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9\s]/.test(password)) score++;

  if (score <= 1) return { level: 1, label: "Weak", color: "#e53935" };
  if (score === 2) return { level: 2, label: "Fair", color: "#fb8c00" };
  if (score === 3) return { level: 3, label: "Good", color: "#fdd835" };
  return { level: 4, label: "Strong", color: "#43a047" };
}

// ── Password strength meter component ────────────────────────
const PasswordStrengthMeter = ({ password }: { password: string }) => {
  if (!password) return null;
  const { level, label, color } = getPasswordStrength(password);

  return (
    <Box sx={{ mt: -1 }}>
      <Box sx={{ display: "flex", gap: 0.5, mb: 0.5 }}>
        {[1, 2, 3, 4].map((seg) => (
          <Box
            key={seg}
            sx={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              backgroundColor: seg <= level ? color : "grey.300",
              transition: "background-color 0.3s ease",
            }}
          />
        ))}
      </Box>
      <Typography variant="caption" sx={{ color }}>
        {label}
      </Typography>
    </Box>
  );
};

// ── Social buttons ────────────────────────────────────────────
const SocialButtons = () => (
  <Box sx={{ width: "100%", mt: 2 }}>
    <Divider sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Or continue with
      </Typography>
    </Divider>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Button
        variant="outlined"
        fullWidth
        startIcon={
          <Avatar sx={{ width: 20, height: 20 }}>
            <Image src="/googlelogo.webp" alt="Google" width={20} height={20} />
          </Avatar>
        }
        href={`${API_BASE}/auth/google`}
        sx={{ borderRadius: 2, textTransform: "none", py: 1 }}
      >
        Continue with Google
      </Button>
      <Button
        variant="outlined"
        fullWidth
        startIcon={
          <Avatar sx={{ width: 20, height: 20 }}>
            <Image
              src="/facebooklogo.png"
              alt="Facebook"
              width={20}
              height={20}
            />
          </Avatar>
        }
        href={`${API_BASE}/auth/facebook`}
        sx={{ borderRadius: 2, textTransform: "none", py: 1 }}
      >
        Continue with Facebook
      </Button>
    </Box>
  </Box>
);

// ── Login panel ───────────────────────────────────────────────
const LoginPanel = ({
  onForgotPassword,
}: {
  onForgotPassword: (email: string) => void;
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const [login, { loading, error }] = useMutation(LOGIN_MUTATION, {
    onCompleted: () => redirectToFeed(),
    onError: (err) => console.log("Leodeo login error:", err),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: string[] = [];
    if (!email) errs.push("Email is required.");
    else if (!emailRe.test(email)) errs.push("Email is invalid.");
    if (!password) errs.push("Password is required.");
    if (errs.length) {
      setErrors(errs);
      return;
    }
    setErrors([]);
    const captchaToken = await executeRecaptcha("login");
    login({
      variables: {
        email: email.trim().toLowerCase(),
        password: password.trim(),
        rememberMe,
        captchaToken,
      },
    });
  };

  return (
    <Box>
      <form onSubmit={handleSubmit} noValidate autoComplete="off">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Email *"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
            fullWidth
            autoFocus
          />
          <TextField
            label="Password *"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  name="remember_me"
                  color="primary"
                />
              }
              label="Remember Me"
            />
            <Typography
              variant="caption"
              sx={{
                cursor: "pointer",
                color: "text.secondary",
                textDecoration: "underline",
              }}
              onClick={() => onForgotPassword(email)}
            >
              FORGOT PASSWORD?
            </Typography>
          </Box>

          {errors.map((err, i) => (
            <Alert key={i} severity="warning">
              {err}
            </Alert>
          ))}
          {(error as any)?.errors?.map(
            ({ message }: { message: string }, i: number) => (
              <Alert key={i} severity="warning">
                {message}
              </Alert>
            ),
          )}
          {loading && <CircularProgress sx={{ alignSelf: "center" }} />}

          <Button
            variant="contained"
            type="submit"
            color="primary"
            fullWidth
            disabled={loading}
            sx={{
              borderRadius: 2,
              py: 1.25,
              textTransform: "none",
              fontSize: "1rem",
            }}
          >
            Login
          </Button>
        </Box>
      </form>
      <SocialButtons />
    </Box>
  );
};

// ── Signup panel ──────────────────────────────────────────────
const SignupPanel = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeatpassword, setRepeatpassword] = useState("");
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [mobile, setMobile] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [thumbnail, setThumbnail] = useState("");
  const [brandName, setBrandName] = useState("");
  const [brandNameEdited, setBrandNameEdited] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Per-signup-session token, scoping the profile photo upload before the
  // account exists (no auth session yet). See CloudinaryUploadService.js
  // (server) for the SIGNUP_PROFILE purpose this pairs with.
  const [signupToken] = useState(() => crypto.randomUUID());

  useEffect(() => {
    if (!brandNameEdited) {
      const derived = `${firstname} ${lastname}`.trim();
      if (derived) setBrandName(derived);
    }
  }, [firstname, lastname, brandNameEdited]);

  const [register, { loading, error }] = useMutation(REGISTER_MUTATION, {
    onCompleted: () => redirectToFeed(),
    onError: (err) => console.log("Leodeo register error:", err),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (!firstname) newErrors.firstname = "First Name is required.";
    if (!lastname) newErrors.lastname = "Last Name is required.";
    if (!email) newErrors.email = "Email is required.";
    else if (!emailRe.test(email)) newErrors.email = "Email is invalid.";
    if (!mobile) newErrors.mobile = "Mobile is required.";
    else if (!mobileRe.test(mobile))
      newErrors.mobile = "Mobile must be 10 digits.";
    if (!password) newErrors.password = "Password is required.";
    else if (!passwordRe.test(password))
      newErrors.password =
        "Min 8 characters with uppercase, lowercase, and a number.";
    if (!repeatpassword)
      newErrors.repeatpassword = "Please repeat your password.";
    else if (password !== repeatpassword)
      newErrors.repeatpassword = "Passwords do not match.";
    if (!thumbnail) newErrors.thumbnail = "Profile picture is required.";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const captchaToken = await executeRecaptcha("register");
    register({
      variables: {
        email,
        password,
        firstname,
        lastname,
        mobile,
        thumbnail,
        brandName,
        captchaToken,
      },
    });
  };

  return (
    <Box>
      <form onSubmit={handleSubmit} noValidate autoComplete="off">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <TextField
              label="First Name *"
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
              variant="outlined"
              fullWidth
              error={!!errors.firstname}
              helperText={errors.firstname}
            />
            <TextField
              label="Last Name *"
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              variant="outlined"
              fullWidth
              error={!!errors.lastname}
              helperText={errors.lastname}
            />
          </Box>
          <TextField
            label="Email *"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
            fullWidth
            error={!!errors.email}
            helperText={errors.email}
          />
          <TextField
            label="Mobile *"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            variant="outlined"
            fullWidth
            error={!!errors.mobile}
            helperText={errors.mobile}
          />
          <TextField
            label="Your brand name"
            value={brandName}
            onChange={(e) => {
              setBrandName(e.target.value);
              setBrandNameEdited(true);
            }}
            variant="outlined"
            fullWidth
            helperText="This is how you'll appear on Leodeo. You can change it later."
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <TextField
              label="Password *"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type={showPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              error={!!errors.password}
              helperText={
                errors.password ||
                "Min 8 characters with uppercase, lowercase, and a number."
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <PasswordStrengthMeter password={password} />
          </Box>
          <TextField
            label="Repeat Password *"
            value={repeatpassword}
            onChange={(e) => setRepeatpassword(e.target.value)}
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            error={!!errors.repeatpassword}
            helperText={errors.repeatpassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Profile photo */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CloudinaryUploadWidget
              signupToken={signupToken}
              onUpload={(url) => {
                setThumbnail(url);
                setErrors((e) => ({ ...e, thumbnail: "" }));
              }}
              buttonTitle="Upload profile photo"
            />
            {thumbnail && (
              <Image
                src={thumbnail}
                alt="Profile preview"
                width={72}
                height={72}
                style={{ borderRadius: "50%", objectFit: "cover" }}
              />
            )}
            {errors.thumbnail && (
              <Alert severity="warning" sx={{ width: "100%" }}>
                {errors.thumbnail}
              </Alert>
            )}
          </Box>

          {loading && <CircularProgress sx={{ alignSelf: "center" }} />}
          {(error as any)?.errors?.map(
            ({ message }: { message: string }, i: number) => (
              <Alert key={i} severity="warning">
                {message}
              </Alert>
            ),
          )}

          <Button
            variant="contained"
            type="submit"
            color="primary"
            fullWidth
            disabled={loading}
            sx={{
              borderRadius: 2,
              py: 1.25,
              textTransform: "none",
              fontSize: "1rem",
            }}
          >
            Create Account
          </Button>
        </Box>
      </form>
      <SocialButtons />
    </Box>
  );
};

// ── Forgot password panel ─────────────────────────────────────
const ForgotPasswordPanel = ({
  initialEmail,
  onBack,
  onSuccess,
}: {
  initialEmail: string;
  onBack: () => void;
  onSuccess: (email: string) => void;
}) => {
  const [email, setEmail] = useState(initialEmail || "");
  const [errorMsg, setErrorMsg] = useState("");

  const [forgotpassword, { loading }] = useMutation(FORGOT_PASSWORD_MUTATION, {
    onCompleted: (data: any) => {
      if (data?.forgotpassword) onSuccess(email.trim().toLowerCase());
      else setErrorMsg("Something went wrong. Please try again.");
    },
    onError: () => setErrorMsg("Something went wrong. Please try again."),
  });

  const handleSubmit = () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setErrorMsg("Please enter your email address.");
      return;
    }
    if (!emailRe.test(trimmed)) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    setErrorMsg("");
    forgotpassword({ variables: { email: trimmed } });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h6">Reset your password</Typography>
      <Typography variant="body2" color="text.secondary">
        Enter your email and we&apos;ll send you a reset link.
      </Typography>
      <TextField
        label="Email *"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSubmit();
        }}
        variant="outlined"
        fullWidth
        disabled={loading}
      />
      {errorMsg && <Alert severity="warning">{errorMsg}</Alert>}
      {loading && <CircularProgress size={24} sx={{ alignSelf: "center" }} />}
      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleSubmit}
        disabled={loading}
        sx={{
          borderRadius: 2,
          py: 1.25,
          textTransform: "none",
          fontSize: "1rem",
        }}
      >
        Send reset link
      </Button>
      <Typography
        variant="body2"
        align="center"
        sx={{
          cursor: "pointer",
          color: "text.secondary",
          textDecoration: "underline",
        }}
        onClick={onBack}
      >
        Back to login
      </Typography>
    </Box>
  );
};

// ── Verify email panel ────────────────────────────────────────
const VerifyEmailPanel = ({
  userId,
  token,
  onSuccess,
}: {
  userId: string;
  token: string;
  onSuccess: () => void;
}) => {
  const [verified, setVerified] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [verifyEmail, { loading }] = useMutation(VERIFY_EMAIL_MUTATION, {
    onCompleted: (data: any) => {
      if (data?.verifyEmail) {
        setVerified(true);
        onSuccess();
      } else
        setErrorMsg(
          "This verification link is invalid or has expired. Please contact support.",
        );
    },
    onError: () => setErrorMsg("Something went wrong. Please try again."),
  });

  useEffect(() => {
    if (userId && token) verifyEmail({ variables: { userId, token } });
  }, [userId, token, verifyEmail]);

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          mt: 4,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Verifying your email…
        </Typography>
      </Box>
    );

  if (errorMsg)
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>
          Verification failed
        </Typography>
        <Alert severity="warning">{errorMsg}</Alert>
      </Box>
    );

  if (verified)
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>
          Email verified
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your account is now verified. Taking you to your feed…
        </Typography>
      </Box>
    );

  return null;
};

// ── Reset password panel ──────────────────────────────────────
const ResetPasswordPanel = ({
  userId,
  jwt,
  onSuccess,
}: {
  userId: string;
  jwt: string;
  onSuccess: () => void;
}) => {
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [userValidated, setUserValidated] = useState(false);

  const [validateUser, { loading: validating, error: validateError }] =
    useMutation(RESET_PASSWORD_MUTATION, {
      onCompleted: (data: any) => setUserValidated(data.resetpassword),
      onError: () => setUserValidated(false),
    });

  const [updatePassword, { loading: updating }] = useMutation(
    RESET_PASSWORD_UPDATE_MUTATION,
    {
      onCompleted: (data: any) => {
        if (data.resetpasswordupdate) onSuccess();
      },
      onError: () => setPasswordError("An error occurred. Please try again."),
    },
  );

  useEffect(() => {
    if (userId && jwt) validateUser({ variables: { userId, jwt } });
  }, [userId, jwt, validateUser]);

  const handleSubmit = () => {
    if (!password1) {
      setPasswordError("Password is required.");
      return;
    }
    if (!password2) {
      setPasswordError("Please re-enter the password.");
      return;
    }
    if (password1 !== password2) {
      setPasswordError("Passwords do not match.");
      return;
    }
    if (!passwordRe.test(password1)) {
      setPasswordError(
        "Min 8 characters with uppercase, lowercase, and a number.",
      );
      return;
    }
    setPasswordError("");
    updatePassword({ variables: { userId, jwt, password: password1 } });
  };

  const adornment = (
    <InputAdornment position="end">
      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  if (validating)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );

  if (validateError || (!validating && !userValidated))
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>
          Invalid or expired link
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please request a new password reset link.
        </Typography>
      </Box>
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h6">Set new password</Typography>
      <TextField
        label="Password *"
        value={password1}
        onChange={(e) => setPassword1(e.target.value)}
        type={showPassword ? "text" : "password"}
        variant="outlined"
        fullWidth
        InputProps={{ endAdornment: adornment }}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSubmit();
        }}
      />
      <TextField
        label="Re-enter password *"
        value={password2}
        onChange={(e) => setPassword2(e.target.value)}
        type={showPassword ? "text" : "password"}
        variant="outlined"
        fullWidth
        InputProps={{ endAdornment: adornment }}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSubmit();
        }}
      />
      <Typography variant="caption" color="text.secondary">
        Min 8 characters with uppercase, lowercase, and a number.
      </Typography>
      {passwordError && <Alert severity="warning">{passwordError}</Alert>}
      {updating && <CircularProgress size={24} sx={{ alignSelf: "center" }} />}
      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleSubmit}
        disabled={updating}
        sx={{
          borderRadius: 2,
          py: 1.25,
          textTransform: "none",
          fontSize: "1rem",
        }}
      >
        Set new password
      </Button>
    </Box>
  );
};

// ── Auth module ───────────────────────────────────────────────
type Mode =
  | "login"
  | "signup"
  | "forgotPassword"
  | "forgotPasswordSuccess"
  | "resetPassword"
  | "resetPasswordSuccess"
  | "verifyEmail";

const AuthModule = ({
  initialMode,
  userId,
  jwt,
}: {
  initialMode: Mode;
  userId: string | null;
  jwt: string | null;
}) => {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [tab, setTab] = useState(0);
  const [forgotEmail, setForgotEmail] = useState("");
  const [successEmail, setSuccessEmail] = useState("");
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    if (mode !== "forgotPasswordSuccess") return;
    if (countdown === 0) {
      setMode("login");
      setTab(0);
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [mode, countdown]);

  useEffect(() => {
    if (mode !== "resetPasswordSuccess") return;
    if (countdown === 0) {
      setMode("login");
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [mode, countdown]);

  const handleForgotPassword = (email: string) => {
    setForgotEmail(email);
    setMode("forgotPassword");
  };
  const handleForgotSuccess = (email: string) => {
    setSuccessEmail(email);
    setCountdown(3);
    setMode("forgotPasswordSuccess");
  };
  const handleResetSuccess = () => {
    setCountdown(3);
    setMode("resetPasswordSuccess");
  };
  const handleVerifySuccess = () => {
    setTimeout(() => redirectToFeed(), 2000);
  };

  if (mode === "verifyEmail" && userId && jwt)
    return (
      <VerifyEmailPanel
        userId={userId}
        token={jwt}
        onSuccess={handleVerifySuccess}
      />
    );

  if (mode === "resetPassword" && userId && jwt)
    return (
      <ResetPasswordPanel
        userId={userId}
        jwt={jwt}
        onSuccess={handleResetSuccess}
      />
    );

  if (mode === "resetPasswordSuccess")
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>
          Password updated
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Returning to login in {countdown}…
        </Typography>
      </Box>
    );

  if (mode === "forgotPassword")
    return (
      <ForgotPasswordPanel
        initialEmail={forgotEmail}
        onBack={() => setMode("login")}
        onSuccess={handleForgotSuccess}
      />
    );

  if (mode === "forgotPasswordSuccess")
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>
          Check your email
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          A reset link has been sent to {successEmail}.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Returning to login in {countdown}…
        </Typography>
      </Box>
    );

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiTab-root": {
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: 600,
          },
        }}
      >
        <Tab label="Login" />
        <Tab label="Create Account" />
      </Tabs>
      {tab === 0 && <LoginPanel onForgotPassword={handleForgotPassword} />}
      {tab === 1 && <SignupPanel />}
    </Box>
  );
};

// ── Leodeo copy ───────────────────────────────────────────────
const LeodeoCopy = () => (
  <Box
    sx={{
      textAlign: { xs: "center", md: "left" },
      mb: { xs: 4, md: 0 },
      pr: { md: 6 },
    }}
  >
    <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
      LEO <span style={{ fontWeight: 400 }}>· Receive</span>
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
      DEO <span style={{ fontWeight: 400 }}>· Give</span>
    </Typography>
    <Typography
      variant="body1"
      sx={{ fontWeight: 500, color: "text.secondary", mb: 0.5 }}
    >
      Share what you make and do.
    </Typography>
    <Typography
      variant="body1"
      sx={{ fontWeight: 500, color: "text.secondary" }}
    >
      Find what you want.
    </Typography>
  </Box>
);

// ── AuthPage (root export) ────────────────────────────────────
export default function AuthPage({
  userId,
  jwt,
  isVerify,
}: {
  userId: string | null;
  jwt: string | null;
  isVerify: boolean;
}) {
  const initialMode: Mode =
    isVerify && userId && jwt
      ? "verifyEmail"
      : userId && jwt
        ? "resetPassword"
        : "login";

  const showCopy = initialMode === "login";

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, md: 4 },
        py: 4,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: 480, md: 900 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 0, md: 8 },
        }}
      >
        {showCopy && (
          <Box sx={{ flex: 1, width: "100%" }}>
            <LeodeoCopy />
          </Box>
        )}
        <Box sx={{ flex: 1, width: "100%", maxWidth: 480 }}>
          <AuthModule initialMode={initialMode} userId={userId} jwt={jwt} />
        </Box>
      </Box>
    </Box>
  );
}
